import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../translations/translations';

const ROHero = () => {
  const { language } = useLanguage();
  const t = translations[language];
  
  return (
    <section className="relative min-h-[85vh] flex items-center justify-center bg-gradient-to-br from-blue-50 via-cyan-50 to-white text-gray-900 overflow-hidden pt-20">
      {/* Animated Water Background */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Water Wave Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute bottom-0 left-0 w-full h-full">
            <svg className="absolute bottom-0 w-full" viewBox="0 0 1440 320" preserveAspectRatio="none" style={{ animation: 'wave-motion 8s ease-in-out infinite' }}>
              <path fill="rgba(59, 130, 246, 0.3)" d="M0,96L48,112C96,128,192,160,288,160C384,160,480,128,576,112C672,96,768,96,864,112C960,128,1056,160,1152,160C1248,160,1344,128,1392,112L1440,96L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"></path>
            </svg>
            <svg className="absolute bottom-0 w-full" viewBox="0 0 1440 320" preserveAspectRatio="none" style={{ animation: 'wave-motion 10s ease-in-out infinite reverse' }}>
              <path fill="rgba(34, 211, 238, 0.2)" d="M0,224L48,213.3C96,203,192,181,288,181.3C384,181,480,203,576,213.3C672,224,768,224,864,213.3C960,203,1056,181,1152,181.3C1248,181,1344,203,1392,213.3L1440,224L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"></path>
            </svg>
          </div>
        </div>

        {/* Animated Water Droplets */}
        {[...Array(15)].map((_, i) => (
          <div
            key={i}
            className="absolute w-3 h-3 bg-blue-400/40 rounded-full"
            style={{
              left: `${10 + (i * 6)}%`,
              top: '-20px',
              animation: `water-drop ${3 + Math.random() * 2}s linear infinite`,
              animationDelay: `${Math.random() * 2}s`,
            }}
          ></div>
        ))}

        {/* Filter Bubbles Animation */}
        {[...Array(8)].map((_, i) => (
          <div
            key={`bubble-${i}`}
            className="absolute w-4 h-4 bg-cyan-300/30 rounded-full"
            style={{
              left: `${15 + (i * 12)}%`,
              bottom: `${10 + (i * 5)}%`,
              animation: `filter-bubble ${2 + Math.random() * 2}s ease-in-out infinite`,
              animationDelay: `${i * 0.3}s`,
            }}
          ></div>
        ))}

        {/* Water Ripples */}
        {[...Array(3)].map((_, i) => (
          <div
            key={`ripple-${i}`}
            className="absolute w-32 h-32 border-2 border-blue-300/30 rounded-full"
            style={{
              left: `${20 + (i * 30)}%`,
              top: `${30 + (i * 20)}%`,
              animation: `water-ripple ${3 + i}s ease-out infinite`,
              animationDelay: `${i * 1.5}s`,
            }}
          ></div>
        ))}
      </div>

      {/* Content */}
      <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <div className="max-w-4xl mx-auto">
          {/* Badge */}
          <div className="inline-block mb-4 px-5 py-2 bg-white/90 backdrop-blur-md rounded-full border-2 border-blue-400/50 shadow-lg shadow-blue-500/20">
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
              <span className="text-xs font-semibold text-blue-700 font-mono">{t.roHeroBadge}</span>
            </div>
          </div>
          
          {/* Main Heading - Compact */}
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-extrabold mb-4 leading-tight">
            <span className="block text-gray-800">{t.roHeroTitle1}</span>
            <span className="block bg-gradient-to-r from-blue-600 via-cyan-600 to-blue-500 bg-clip-text text-transparent">
              {t.roHeroTitle2}
            </span>
          </h1>
          
          <p className="text-lg md:text-xl mb-3 text-gray-700 font-medium">
            {t.roHeroSubtitle}
          </p>
          <p className="text-base md:text-lg mb-8 text-gray-600 max-w-2xl mx-auto">
            {t.roHeroDescription}
          </p>
          
          {/* CTA Buttons - Compact */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-10">
            <a 
              href="#services" 
              className="group relative px-8 py-4 bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-xl font-bold text-base overflow-hidden transition-all duration-300 transform hover:scale-105 shadow-xl shadow-blue-500/40 border-2 border-blue-500/50"
            >
              <span className="relative z-10 flex items-center">
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z"/>
                </svg>
                {t.roHeroButton1}
              </span>
            </a>
            <a 
              href="#contact" 
              className="px-8 py-4 bg-white border-2 border-blue-500 text-blue-600 rounded-xl font-bold text-base hover:bg-blue-50 transition-all duration-300 transform hover:scale-105 shadow-lg"
            >
              {t.roHeroButton2}
            </a>
          </div>

          {/* Stats - Compact Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto">
            {[
              { number: '500+', label: t.roHeroStat1, icon: '💧' },
              { number: '1000+', label: t.roHeroStat2, icon: '🏆' },
              { number: '15+', label: t.roHeroStat3, icon: '⭐' },
              { number: '24/7', label: t.roHeroStat4, icon: '🔧' }
            ].map((stat, index) => (
              <div key={index} className="bg-white/80 backdrop-blur-sm rounded-xl p-4 border-2 border-blue-200/50 transform hover:scale-105 transition-all duration-300 hover:border-blue-400/70 hover:shadow-lg">
                <div className="text-2xl mb-1">{stat.icon}</div>
                <div className="text-2xl md:text-3xl font-bold mb-1 text-blue-600 font-mono">{stat.number}</div>
                <div className="text-xs text-gray-600 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Scroll Indicator - Compact */}
      <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 animate-bounce">
        <div className="flex flex-col items-center">
          <span className="text-xs mb-1 text-blue-600 font-mono">{t.roHeroScroll}</span>
          <svg className="w-5 h-5 text-blue-500" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
            <path d="M19 14l-7 7m0 0l-7-7m7 7V3"></path>
          </svg>
        </div>
      </div>
    </section>
  );
};

export default ROHero;
